// public/scripts/payment-client.js
// Payment UI module — Stripe Payment Element integration for tier upgrade

import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";
import { openModal, closeModal } from './index-ui.js';

// Stripe publishable key (safe for client-side — can only create tokens, not charge)
const STRIPE_PUBLISHABLE_KEY = 'pk_test_placeholder';

let functionsRef = null;
let stripe = null;
let elements = null;
let paymentElement = null;
let isSubmitting = false;

/**
 * Initialize the payment module.
 * @param {object} functions - Firebase Functions instance
 */
export function initPayment(functions) {
  functionsRef = functions;

  // Wire up modal close buttons
  const upgradeModal = document.getElementById('upgrade-modal');
  const successModal = document.getElementById('upgrade-success-modal');

  if (upgradeModal) {
    const closeBtn = upgradeModal.querySelector('.close-button');
    if (closeBtn) closeBtn.addEventListener('click', dismissUpgradePrompt);

    const cancelBtn = document.getElementById('pay-cancel-btn');
    if (cancelBtn) cancelBtn.addEventListener('click', dismissUpgradePrompt);

    // Close on background click
    upgradeModal.addEventListener('click', (e) => {
      if (e.target === upgradeModal) dismissUpgradePrompt();
    });

    const payBtn = document.getElementById('pay-now-btn');
    if (payBtn) payBtn.addEventListener('click', handlePaySubmit);
  }

  if (successModal) {
    const closeBtn = successModal.querySelector('.close-button');
    if (closeBtn) closeBtn.addEventListener('click', () => closeModal('upgrade-success-modal'));

    const goBtn = document.getElementById('upgrade-success-close-btn');
    if (goBtn) goBtn.addEventListener('click', () => closeModal('upgrade-success-modal'));

    successModal.addEventListener('click', (e) => {
      if (e.target === successModal) closeModal('upgrade-success-modal');
    });
  }
}

/**
 * Show the upgrade prompt modal and mount the Stripe Payment Element.
 */
export async function showUpgradePrompt() {
  const errorEl = document.getElementById('payment-error');
  const payBtn = document.getElementById('pay-now-btn');
  if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
  if (payBtn) payBtn.disabled = true;

  openModal('upgrade-modal');

  try {
    // Initialize Stripe if not already done
    if (!stripe) {
      stripe = Stripe(STRIPE_PUBLISHABLE_KEY);
    }

    // Create PaymentIntent via server
    const createPaymentIntent = httpsCallable(functionsRef, 'createPaymentIntent');
    const { data } = await createPaymentIntent();
    const { clientSecret } = data;

    // Clean up previous Payment Element if any
    if (paymentElement) {
      paymentElement.destroy();
      paymentElement = null;
      elements = null;
    }

    // Mount Payment Element
    elements = stripe.elements({ clientSecret });
    paymentElement = elements.create('payment');
    paymentElement.mount('#payment-element');

    // Enable pay button once element is ready
    paymentElement.on('ready', () => {
      if (payBtn) payBtn.disabled = false;
    });

  } catch (err) {
    console.error('payment-client: Error setting up payment:', err);
    showPaymentError(getErrorMessage(err));
  }
}

/**
 * Handle the Pay button click — confirm payment with Stripe.
 */
async function handlePaySubmit() {
  if (isSubmitting || !elements) return;
  isSubmitting = true;

  const payBtn = document.getElementById('pay-now-btn');
  const errorEl = document.getElementById('payment-error');
  if (payBtn) { payBtn.disabled = true; payBtn.textContent = 'Processing...'; }
  if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }

  try {
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      showPaymentError(error.message);
      if (payBtn) { payBtn.disabled = false; payBtn.textContent = 'Pay $4.99'; }
    } else {
      // Payment succeeded or is processing — webhook will update tier server-side
      closeModal('upgrade-modal');
      openModal('upgrade-success-modal');

      // Dispatch tier-changed event so other UI components can react
      window.dispatchEvent(new CustomEvent('krida-tier-changed', { detail: { tier: 'paid' } }));
    }
  } catch (err) {
    console.error('payment-client: Payment confirmation error:', err);
    showPaymentError(getErrorMessage(err));
    if (payBtn) { payBtn.disabled = false; payBtn.textContent = 'Pay $4.99'; }
  } finally {
    isSubmitting = false;
  }
}

/**
 * Dismiss the upgrade prompt — close modal, no side effects.
 */
function dismissUpgradePrompt() {
  closeModal('upgrade-modal');
  // Clean up Stripe elements to avoid stale state on re-open
  if (paymentElement) {
    paymentElement.destroy();
    paymentElement = null;
    elements = null;
  }
}

/**
 * Display an error message in the payment modal.
 */
function showPaymentError(message) {
  const errorEl = document.getElementById('payment-error');
  if (errorEl) {
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  }
}

/**
 * Extract a user-friendly error message from Firebase/Stripe errors.
 */
function getErrorMessage(err) {
  if (err.code === 'functions/already-exists' || err.code === 'already-exists') {
    return 'You already have a paid account. Please refresh the page.';
  }
  if (err.code === 'functions/resource-exhausted' || err.code === 'resource-exhausted') {
    return 'Too many payment attempts. Please try again later.';
  }
  if (err.code === 'functions/unauthenticated' || err.code === 'unauthenticated') {
    return 'Please sign in to make a payment.';
  }
  return err.message || 'An unexpected error occurred. Please try again.';
}
